import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    try {
      if (!isHomePage) {
        window.location.href = `/#${sectionId}`;
        return;
      }
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } catch (error) {
      console.error('Failed to scroll to section:', error);
    }
  };

  const navLinks = [
    { label: 'Services', to: '/services' },
    { label: 'About Us', to: '/about' },
    { label: 'Resources', to: '/resources' },
    { label: 'Contact', to: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5">
          <div
            className={`px-4 py-3 rounded-2xl backdrop-blur-[10px] flex justify-between items-center transition-all duration-300 ${
              isScrolled ? 'bg-white shadow-lg' : 'bg-black/25'
            }`}
          >
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/9b819ec02f0cc35e99e8e09b39e5fe1e/4299ef1f9e55f3d4ef76bc3b6e5af17b.png"
                alt="CJ Solutions Logo"
                width={120}
                height={40}
                fetchPriority="high"
                decoding="sync"
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <h1
                  className={`text-lg font-bold transition-colors ${
                    isScrolled ? 'text-slate-900' : 'text-white'
                  }`}
                >
                  CJ Solutions
                </h1>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-lg font-medium transition-colors whitespace-nowrap group ${
                    isActive(link.to)
                      ? 'text-amber-500'
                      : isScrolled
                      ? 'text-slate-700 hover:text-amber-500'
                      : 'text-white hover:text-amber-400'
                  }`}
                >
                  {link.label}
                  {/* Active underline indicator */}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-amber-500 rounded-full transition-all duration-300 ${
                      isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center">
              <Link
                to="/contact"
                className="px-6 py-3 bg-amber-500 text-white text-lg font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Consultation
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i
                className={`ri-menu-line text-2xl ${
                  isScrolled ? 'text-slate-700' : 'text-white'
                }`}
              ></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-slate-900 z-50">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-6">
              <img
                src="https://static.readdy.ai/image/9b819ec02f0cc35e99e8e09b39e5fe1e/4299ef1f9e55f3d4ef76bc3b6e5af17b.png"
                alt="CJ Solutions Logo"
                className="h-10 w-auto"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white p-2 cursor-pointer"
              >
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>

            <nav className="flex-1 px-6 py-8">
              <div className="space-y-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 text-2xl font-medium transition-colors ${
                      isActive(link.to)
                        ? 'text-amber-500'
                        : 'text-white hover:text-amber-500'
                    }`}
                  >
                    {isActive(link.to) && (
                      <span className="w-1.5 h-6 bg-amber-500 rounded-full inline-block" />
                    )}
                    {link.label}
                  </Link>
                ))}
              </div>
            </nav>

            <div className="p-6">
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full py-4 bg-amber-500 text-white text-xl font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                Get Free Consultation
              </Link>
            </div>

            <div className="p-6 text-center">
              <p className="text-white/50 text-lg">
                © 2025,{' '}
                <span className="text-white underline">CJ Solutions</span>. All
                Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
